/**
 * CR-5858: Customer-side reporting panel for "Read & Understood" confirmations.
 *
 * Surfaces the company_confirmation_report endpoint inside the
 * supplier-detail Documents tab — shows summary counts and a per-company
 * table (confirmed / stale / outstanding) for the supplier companies that
 * owe a confirmation. Customers only; suppliers can't enumerate other
 * suppliers' status (enforced on the backend).
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck, AlertCircle, Clock } from 'lucide-react';
import { Badge } from '../../components/ui/badge';
import {
  documentsAPI,
  type Document as DocType,
  type CompanyConfirmationReport,
} from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';

interface CompanyConfirmationPanelProps {
  document: DocType;
}

const STATUS_CONFIG: Record<string, { label: string; className: string; Icon: typeof ShieldCheck }> = {
  ['confirmed']: {
    label: 'Confirmed',
    className: 'bg-green-100 text-green-800 hover:bg-green-100',
    Icon: ShieldCheck,
  },
  ['stale']: {
    label: 'Awaiting reconfirmation',
    className: 'bg-orange-100 text-orange-800 hover:bg-orange-100',
    Icon: Clock,
  },
  ['outstanding']: {
    label: 'Outstanding',
    className: 'bg-red-100 text-red-800 hover:bg-red-100',
    Icon: AlertCircle,
  },
};

export function CompanyConfirmationPanel({ document: doc }: CompanyConfirmationPanelProps) {
  const { formatDate } = useFormatters();
  const [report, setReport] = useState<CompanyConfirmationReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    documentsAPI
      .getCompanyConfirmationReport(doc.id)
      .then((res) => {
        if (!cancelled) setReport(res.data);
      })
      .catch((err: unknown) => {
        console.error('Failed to load company confirmation report:', err);
        if (!cancelled) setError('Could not load confirmation report.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [doc.id, doc.confirmation_version_token]);

  if (!doc.requires_company_confirmation) return null;

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-semibold flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-amber-600" />
        Read &amp; Understood Confirmations
      </h3>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading report…
        </div>
      ) : error ? (
        <p className="text-sm text-red-600">{error}</p>
      ) : report ? (
        <>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="rounded-md border p-2">
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-lg font-semibold">{report.summary.total}</p>
            </div>
            <div className="rounded-md border p-2 bg-green-50/60">
              <p className="text-xs text-green-700">Confirmed</p>
              <p className="text-lg font-semibold text-green-700">{report.summary.confirmed}</p>
            </div>
            <div className="rounded-md border p-2 bg-orange-50/60">
              <p className="text-xs text-orange-700">Stale</p>
              <p className="text-lg font-semibold text-orange-700">{report.summary.stale}</p>
            </div>
            <div className="rounded-md border p-2 bg-red-50/60">
              <p className="text-xs text-red-700">Outstanding</p>
              <p className="text-lg font-semibold text-red-700">{report.summary.outstanding}</p>
            </div>
          </div>

          {/* Per-company rows */}
          {report.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">
              This document is not shared with any supplier companies yet.
            </p>
          ) : (
            <div className="border rounded-md divide-y">
              {report.rows.map((row) => {
                const config = STATUS_CONFIG[row.status] || STATUS_CONFIG['outstanding'];
                const Icon = config.Icon;
                return (
                  <div key={row.company_id} className="px-3 py-2 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate" title={row.company_name}>
                        {row.company_name}
                      </p>
                      {row.confirmed_by_name && row.confirmed_at && (
                        <p className="text-xs text-muted-foreground truncate">
                          {row.confirmed_by_name} • {formatDate(row.confirmed_at)}
                          {row.version_label ? ` • v${row.version_label}` : ''}
                        </p>
                      )}
                    </div>
                    <Badge className={`text-xs flex items-center gap-1 ${config.className}`}>
                      <Icon className="h-3 w-3" />
                      {config.label}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}
